import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";

const PublicPortalConfigPage = () => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const uniqueId = taxPayer.uniqueId ?? null;

  const portalUrl = uniqueId
    ? `${origin}/consulta/${encodeURIComponent(uniqueId)}`
    : null;
  const iframeSnippet = portalUrl
    ? `<iframe src="${portalUrl}" width="100%" height="600" frameborder="0" style="border:0"></iframe>`
    : null;

  if (!uniqueId) {
    return (
      <div style={{ padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Portal Público de Consulta</h1>
        <p>
          El identificador del contribuyente todavía no está disponible.
          Contacte al soporte de SmartDoc para habilitarlo.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 32, maxWidth: 920 }}>
      <h1 style={{ marginTop: 0 }}>Portal Público de Consulta</h1>
      <p style={{ color: "var(--theme-text-soft)", lineHeight: 1.55 }}>
        El Portal Público de Consulta permite que sus clientes verifiquen y
        descarguen los documentos electrónicos que usted les emitió (facturas,
        notas de crédito, notas de débito, notas de remisión y autofacturas),
        sin necesidad de iniciar sesión. Para acceder ingresan su RUC en el
        formato <code>xxxxxxxx-x</code> y el número del documento en el formato{" "}
        <code>xxx-xxx-xxxxxxx</code>.
      </p>

      <Section
        title="1. Enlace para compartir"
        description={
          <>
            Comparta este enlace con sus clientes (por correo, WhatsApp, o
            publicado en su sitio). Al abrirlo verán el formulario de consulta
            con su logo y nombre comercial.
          </>
        }
        value={portalUrl!}
        label="URL del portal"
      />

      <Section
        title="2. Insertar en su sitio (iframe)"
        description={
          <>
            Si prefiere incrustar el portal dentro de su propia página web,
            copie el siguiente código HTML y péguelo donde quiera que aparezca
            el formulario. Se mostrará la misma página, ajustándose al ancho
            del contenedor.
          </>
        }
        value={iframeSnippet!}
        label="Código HTML para incrustar"
        multiline
      />

      <div style={{ marginTop: 32, fontSize: "0.9rem", color: "var(--theme-muted)" }}>
        Nota: Por seguridad, los enlaces de descarga del KUDE expiran a los
        pocos minutos. El cliente puede volver a buscar el documento cuando lo
        necesite — el portal estará siempre disponible.
      </div>
    </div>
  );
};

interface SectionProps {
  title: string;
  description: React.ReactNode;
  value: string;
  label: string;
  multiline?: boolean;
}

const Section: React.FC<SectionProps> = ({
  title,
  description,
  value,
  label,
  multiline,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback: select the text in a hidden textarea
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section
      style={{
        marginTop: 28,
        padding: 20,
        background: "var(--theme-surface)",
        border: "1px solid var(--theme-border)",
        borderRadius: 6,
      }}
    >
      <h2 style={{ margin: "0 0 8px 0", fontSize: "1.15rem" }}>{title}</h2>
      <p style={{ margin: "0 0 14px 0", color: "var(--theme-text-soft)", lineHeight: 1.5 }}>
        {description}
      </p>
      <label
        style={{
          display: "block",
          fontWeight: 600,
          fontSize: "0.85rem",
          marginBottom: 4,
          color: "var(--theme-text-soft)",
        }}
      >
        {label}
      </label>
      <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
        {multiline ? (
          <textarea
            readOnly
            value={value}
            onFocus={(e) => e.currentTarget.select()}
            rows={3}
            style={{
              flex: 1,
              fontFamily: "monospace",
              fontSize: "0.85rem",
              padding: "8px 10px",
              border: "1px solid var(--theme-border)",
              borderRadius: 4,
              resize: "vertical",
            }}
          />
        ) : (
          <input
            readOnly
            value={value}
            onFocus={(e) => e.currentTarget.select()}
            style={{
              flex: 1,
              fontFamily: "monospace",
              fontSize: "0.9rem",
              padding: "8px 10px",
              border: "1px solid var(--theme-border)",
              borderRadius: 4,
            }}
          />
        )}
        <button
          type="button"
          onClick={handleCopy}
          style={{
            padding: "0 16px",
            background: copied ? "#1f8a5b" : "#006657",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {copied ? "¡Copiado!" : "Copiar"}
        </button>
      </div>
    </section>
  );
};

export default PublicPortalConfigPage;
